import { currencyShort } from '../components/ui'
import { CurrencyControl } from '../components/CurrencyControl'
import { useReportCurrency } from '../components/rememberedCurrency'
import { MIXED_UNITS, mixedUnitsHint } from '../components/units'
import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Api } from '../api'
import { PARTIAL_NOTE, PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE, partialOf } from '../api/answer'
import { Withheld, markPartial } from '../components/Withheld'
import { ApiError, LOCAL, USD, inCurrency, isConverted, isUsd, toParam } from '../api/types'
import type {
  ApiFailure, Currency, CurrencyRate, InvAgeBand, InvExpiryBand, InvFilterKind, InvFilterRow,
  InvDrillPath, InvGroupBy, InvLotRow, InvMatrixRow, InvOverview, InvWindow, StoredReaderView,
} from '../api/types'
import { DataGrid, type Column } from '../components/DataGrid'
import { restoreSort, snapshotSort, useSortMirror, type SortState } from '../components/gridSort'
import { ExpandableCard } from '../components/ExpandableCard'
import { DevModeProvider } from '../components/MeasurePanel'
import { Explain, ExplainMark } from '../components/DevModeContext'
import { ExportButtons } from '../components/ExportButtons'
import { FailureState } from '../components/FailureState'
import { currencyMark } from '../components/CurrencyHover'
import { GridChrome, ResizeHandle, applyLayout, restore, snapshot, useGridLayout } from '../components/gridLayout'
import { gridsOnTab } from '../api/viewScope'
import {
  NOTHING_DROPPED,
  SavedViewsMenu,
  VIEW_SCHEMA,
  ViewAdjustedNotice,
  parseView,
  useSavedViews,
  useViewDirty,
  type DropReport,
  type ViewPayload,
} from '../components/SavedViews'
import type { CellKind, ExportDoc, ExportRow } from '../export/model'
import {
  Button, CollapsibleStats, EmptyState, Loading, PageTabs, ReportHeader,
  SearchInput, SearchSelect, Segmented, StatTile, compact, money,
} from '../components/ui'

/**
 * INVENTORY AGING — three Power BI inventory reports, rebuilt as one.
 *
 * The Inventory Dashboard, its V2 and the Operations version all read one view
 * over the stock lots, and about 26 of their pages collapse into three screens
 * (owner, 4 Oct 2026 — D1):
 *
 *   Aging    how long stock has been held, in the view's own buckets, grouped
 *            by company, business unit, business team, product or lot
 *   Expiry   lots by when they expire: expired, within 30 / 90 / 180 days,
 *            no expiry date, or all
 *   Lots     every lot with every attribute it carries
 *
 * QUANTITY FOR EVERYONE WHO CAN OPEN IT, MONEY ONLY WITH INVENTORY COST (D2).
 * Quantities follow the report's own gate (Inv_Mod), so they are never masked
 * for a reader who got this far; value, the value buckets and unit cost follow
 * Inv_Cost. The overview SAYS which — `canSeeCost` — and the money columns are
 * left off the grids for a reader without it, rather than drawn as a wall of
 * dashes. That reader is the Power BI Operations version's audience.
 *
 * THE RULES ARE THE PROCEDURE'S (040), and the screen only names them:
 *   - age counts from arrival; drop-ship stock and stock with no known arrival
 *     have their own columns, so the buckets always add up to the total;
 *   - expired is before today, the 1 Jan 1900 placeholder included, as Power BI
 *     counted it (D3); expiring today is within 30 days, not expired;
 *   - days are a lot's own and are never summed up a level; a group shows its
 *     oldest arrival and earliest expiry instead;
 *   - unit cost is value ÷ quantity, never a sum of unit costs.
 */

type TabId = 'aging' | 'expiry' | 'lots'

const TABS: { id: TabId; label: string; title: string }[] = [
  { id: 'aging', label: 'Aging', title: 'Stock by how long it has been held, grouped by company, unit, team, product or lot' },
  { id: 'expiry', label: 'Expiry', title: 'Lots by when they expire: expired, within 30, 90 or 180 days, 6 to 12 months and beyond, or with no expiry date' },
  { id: 'lots', label: 'Lots', title: 'Every lot with every attribute: dates, unit, warehouse, supplier, quantity and value' },
]

/* 'productwh' is Power BI V1's product page: one row per product per warehouse. */
const GROUP_LABEL: Record<InvGroupBy, string> = {
  company: 'Company', bu: 'Business unit', bt: 'Business team', product: 'Product',
  productwh: 'Product by warehouse', lot: 'Lot',
}

/* Past to future, then the two that are not a window of days. The three past
   180 days are BANDS, as the Operations report's Expiry Period slicer offered
   them: "8–12 months" does not repeat the lots of "6–8 months". */
const WINDOW_LABEL: Record<InvWindow, string> = {
  expired: 'Expired', within30: 'Within 30 days', within90: 'Within 90 days', within180: 'Within 180 days',
  months6to8: '6–8 months', months8to12: '8–12 months', over12months: 'Over 12 months',
  noexpiry: 'No expiry date', all: 'All lots',
}
const WINDOW_TITLE: Record<InvWindow, string> = {
  expired: 'Expiry date before today, including lots dated 1 Jan 1900, a placeholder Power BI also counted as expired',
  within30: 'Expiring today through 30 days from now',
  within90: 'Expiring today through 90 days from now',
  within180: 'Expiring today through 180 days from now',
  months6to8: 'Expiring 181 to 240 days from now',
  months8to12: 'Expiring 241 to 365 days from now',
  over12months: 'Expiring more than 365 days from now',
  noexpiry: 'Lots with no expiry date recorded at all',
  all: 'Every lot, whatever its expiry',
}

type Measure = 'value' | 'quantity'
/** The Aging screen's two grids: the drill-down (first) and the flat list. */
type AgingView = 'drill' | 'list'
/* Each keeps its own column arrangement; a saved view carries both. */
const DRILL_GRID = 'inv-aging-drill'
const LIST_GRID = 'inv-aging'
/** The drill-down's row column holds every level, so its header names them all. */
const DRILL_CAPTION = 'Company / Unit / Team / Product / Lot'

/** The eight age buckets in their range order (Power BI sorted two of them by text, B6), then the two outside any range. */
const BUCKETS: { band: InvAgeBand; header: string; title?: string }[] = [
  { band: '0_30', header: '0–30 Days' },
  { band: '31_60', header: '31–60 Days' },
  { band: '61_90', header: '61–90 Days' },
  { band: '91_120', header: '91–120 Days' },
  { band: '121_180', header: '121–180 Days' },
  { band: '181_240', header: '181–240 Days' },
  { band: '241_365', header: '241–365 Days' },
  { band: 'over365', header: 'Over 365 Days' },
  { band: 'drop', header: 'Drop-Ship', title: 'Stock in a drop-ship warehouse. In no age bucket, as in Power BI.' },
  { band: 'unknown', header: 'Arrival Unknown', title: 'No arrival date, or one in the future, so no age can be counted.' },
]
/** The matrix field for a bucket, in quantity (q…) or value (v…). */
const BUCKET_FIELD: Record<InvAgeBand, { q: keyof InvMatrixRow; v: keyof InvMatrixRow }> = {
  '0_30': { q: 'q0_30', v: 'v0_30' },
  '31_60': { q: 'q31_60', v: 'v31_60' },
  '61_90': { q: 'q61_90', v: 'v61_90' },
  '91_120': { q: 'q91_120', v: 'v91_120' },
  '121_180': { q: 'q121_180', v: 'v121_180' },
  '181_240': { q: 'q181_240', v: 'v181_240' },
  '241_365': { q: 'q241_365', v: 'v241_365' },
  over365: { q: 'qOver365', v: 'vOver365' },
  drop: { q: 'qDrop', v: 'vDrop' },
  unknown: { q: 'qUnknown', v: 'vUnknown' },
}

const AGE_LABEL: Record<InvAgeBand, string> = {
  '0_30': '0–30 days', '31_60': '31–60 days', '61_90': '61–90 days', '91_120': '91–120 days',
  '121_180': '121–180 days', '181_240': '181–240 days', '241_365': '241–365 days', over365: 'Over 365 days',
  drop: 'Drop-ship', unknown: 'Arrival unknown',
}
const EXPIRY_LABEL: Record<InvExpiryBand, string> = {
  expired: 'Expired', '0_30': 'Within 30 days', '31_60': '31–60 days', '61_90': '61–90 days',
  '91_120': '91–120 days', '121_180': '121–180 days', '181_240': '181–240 days', '241_365': '241–365 days',
  over365: 'Over 12 months', none: 'No expiry date',
}

const PAGE_SIZE = 100
const DATE_FMT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }

/**
 * WHAT A FIGURE IS IN — the same three states Material Planning draws: one
 * unit, a mixture ("mixed units", explained on hover in the words Sales and
 * Material Planning use), or nothing recorded.
 */
interface Dim { text: string; title?: string; currency?: string; mixed?: boolean }
const MIXED_CURRENCIES = 'mixed currencies'
const dimOf = (value: string | null | undefined, count?: number | null, kind: 'unit' | 'currency' = 'unit'): Dim | null => {
  if (count !== null && count !== undefined && count > 1) {
    return kind === 'unit'
      ? { text: MIXED_UNITS, title: mixedUnitsHint([], count), mixed: true }
      : {
          text: MIXED_CURRENCIES,
          title: `Adds ${count} currencies together as booked, so this is not one amount. Choose USD or Custom to see it in one currency.`,
          mixed: true,
        }
  }
  return value ? { text: value } : null
}
const curOf = (value: string | null | undefined, count?: number | null): Dim | null => {
  const d = dimOf(value, count, 'currency')
  return d && value && !d.mixed ? { ...d, currency: value } : d
}
const withDim = (existing: string | undefined, d: Dim | null): ReactNode => {
  if (!d) return existing || undefined
  const word = d.mixed ? <span title={d.title}>{d.text}</span> : d.text
  return existing ? <>{existing} · {word}</> : word
}
const withCur = (existing: string | undefined, d: Dim | null): ReactNode =>
  d?.currency
    ? existing ? <>{existing} · {currencyMark(d.currency)}</> : currencyMark(d.currency)
    : withDim(existing, d)

function Suffix({ d }: { d: Dim | null | undefined }) {
  if (!d) return null
  return (
    <span
      className="ml-1 inline-block min-w-[2.5ch] text-left text-(length:--text-small) font-normal text-(--color-ink-3)"
      title={d.title}
      data-currency={d.currency}
    >
      {d.text}
    </span>
  )
}

/** What the "This page" row is in: a total over several units says so, and why. */
function totalsOf(units: (string | null | undefined)[]): { totalsLabel: string; totalsTitle?: string } {
  const set = new Set(units.filter((u): u is string => !!u))
  if (set.size <= 1 && !set.has(MIXED_UNITS)) return { totalsLabel: 'This page' }
  return {
    totalsLabel: `This page · ${MIXED_UNITS}`,
    totalsTitle: mixedUnitsHint([...set].filter((u) => u !== MIXED_UNITS)),
  }
}

/* UNDER TODAY'S MODEL QUANTITIES ARE NEVER MASKED HERE (they follow the report's
   own gate), so an empty quantity cell can only mean nothing was recorded. Money
   can be withheld, but a reader without cost never sees a money column at all —
   see the header. UNDER THE NEW MODEL a blank figure is withheld, and only that
   (054; M8): a figure the reader may see over no lots is 0. The cells ask which
   model answered, so every helper below draws both. */
const NewModel = createContext(false)
function Blank({ money: isMoney }: { money: boolean }) {
  const v2 = useContext(NewModel)
  if (v2) return <Withheld />
  return isMoney
    ? <span className="text-(--color-ink-4)" title="Not shown to you">—</span>
    : <span className="text-(--color-ink-4)" title="Not recorded in the source">–</span>
}
const ABSENT = <Blank money={false} />
const MASKED = <Blank money />
/** A price over no quantity: nothing to divide by, which nothing withheld. */
const NO_QUANTITY = <span className="text-(--color-ink-4)" title="No quantity to divide by">–</span>

/**
 * A QUANTITY IN A GRID IS THE WHOLE NUMBER (5 Oct 2026). The tile style
 * ("1.2K kg") cost a third of the lots their last digits — 4,138 of 12,080 hold
 * a thousand units or more — and the Power BI Operations report printed lot
 * quantities to four decimals for the people who count stock. Four places, as
 * that report did: 438 lots carry three or four, none carry more. The tiles
 * stay short; a headline is not a stock count.
 */
const qtyText = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 4 })
const num = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? ABSENT
    : <><span className={`tabular-nums ${v < 0 ? 'text-(--color-overdue)' : ''}`}>{qtyText(v)}</span><Suffix d={d} /></>
const cash = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? MASKED
    : <><span className={`tabular-nums ${v < 0 ? 'text-(--color-overdue)' : ''}`}>{money(v)}</span><Suffix d={d} /></>
/**
 * A UNIT PRICE KEEPS ITS DECIMALS. `money` rounds to whole units, which is right
 * for a total and wrong for a price: a lot costing 4.93 a kilogram read "5", and
 * below one unit (a resin at 0.12 a gram) every price read "0". Two places, four
 * under one, so neither case loses what it is.
 */
const priceText = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(v) < 1 ? 4 : 2 })
const price = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? MASKED
    : <><span className="tabular-nums">{priceText(v)}</span><Suffix d={d} /></>
/** A bucket cell: a zero is drawn quietly, so the buckets that hold stock stand out across the row. */
const bucket = (v: number | null | undefined, measure: Measure) =>
  v === null || v === undefined ? (measure === 'value' ? MASKED : ABSENT)
    : v === 0 ? <span className="tabular-nums text-(--color-ink-4)">0</span>
    : <span className={`tabular-nums ${v < 0 ? 'text-(--color-overdue)' : ''}`}>{measure === 'value' ? money(v) : qtyText(v)}</span>

/** The 1 Jan 1900 expiry, a placeholder in the source that counts as expired (D3). */
const isPlaceholder = (v: string | null | undefined) => !!v && v.startsWith('1900-01-01')
const PLACEHOLDER = (
  <span className="text-(--color-ink-3)" title="1 Jan 1900 is a placeholder in the source, not a real date. Counted as expired, as Power BI counted it.">
    1900 placeholder
  </span>
)
const date = (v: string | null | undefined) =>
  !v ? <span className="text-(--color-ink-4)">—</span>
    : isPlaceholder(v) ? PLACEHOLDER
    : new Date(v).toLocaleDateString('en-GB', DATE_FMT)
const dateS = (v: string | null | undefined) =>
  !v ? null : isPlaceholder(v) ? '1900 placeholder' : new Date(v).toLocaleDateString('en-GB', DATE_FMT)

/** Days to expiry: red once expired; the placeholder's forty-thousand-odd days are not a number anybody should read. */
const toExpire = (v: number | null | undefined, expiry: string | null | undefined) =>
  isPlaceholder(expiry) ? PLACEHOLDER
    : v === null || v === undefined ? <span className="text-(--color-ink-4)" title="No expiry date">—</span>
    : <span className={`tabular-nums ${v < 0 ? 'font-semibold text-(--color-overdue)' : ''}`}>{v.toLocaleString()}</span>
/** Days since arrival. Negative means the arrival is in the future. */
const daysOld = (v: number | null | undefined) =>
  v === null || v === undefined ? <span className="text-(--color-ink-4)" title="No arrival date">—</span>
    : v < 0 ? <span className="text-(--color-ink-3)" title="The arrival date is in the future">arrives in {(-v).toLocaleString()}</span>
    : <span className="tabular-nums">{v.toLocaleString()}</span>

const numS = (v: number | null | undefined, v2 = false) => (v === null || v === undefined ? (v2 ? WITHHELD : '–') : compact(v))
const cashS = (v: number | null | undefined, v2 = false) => (v === null || v === undefined ? (v2 ? WITHHELD : '—') : money(v))
const fullQty = (v: number | null | undefined, v2 = false) =>
  v === null || v === undefined ? (v2 ? WITHHELD_TITLE : 'Not recorded in the source') : v.toLocaleString()
const fullCash = (v: number | null | undefined, v2 = false) =>
  v === null || v === undefined ? (v2 ? WITHHELD_TITLE : 'Not shown to you') : money(v)
/** A tile that leaves withheld lots out keeps its figure, gains the mark, and says why on hover (M8). */
const partS = (s: string, partial: boolean) => (partial ? `${s}*` : s)
const partF = (s: string, partial: boolean) => (partial ? `${s}. ${PARTIAL_TITLE}.` : s)

/** The figure a grid column shows, by the name the new model's Partial uses; null for a column that shows none. */
const figureOf = (key: string, value: boolean): keyof InvMatrixRow | null =>
  key.startsWith('b') && key.slice(1) in BUCKET_FIELD ? BUCKET_FIELD[key.slice(1) as InvAgeBand][value ? 'v' : 'q']
    : key === 'total' ? (value ? 'value' : 'quantity')
    : key === 'quantity' || key === 'value' || key === 'unitCost' ? key
    : null

/** Under the new model each figure column marks a partly withheld cell and tells the totals row what is withheld (M8). */
function withAnswer(v2: boolean, value: boolean, cols: Column<InvMatrixRow>[]): Column<InvMatrixRow>[] {
  if (!v2) return cols
  return cols.map((c) => {
    const f = figureOf(c.key, value)
    if (!f) return c
    const render = c.render
    return {
      ...c,
      render: render && ((r: InvMatrixRow) => markPartial(partialOf(r).has(f), render(r))),
      withheld: (r: InvMatrixRow) => (r[f] === null ? 'withheld' : partialOf(r).has(f) ? 'partial' : null),
    }
  })
}
const share = (part: number | null | undefined, whole: number | null | undefined) =>
  part === null || part === undefined || !whole ? undefined : `${((part / whole) * 100).toFixed(1)}% of the total`

/** The standing filters: the four slicers every Power BI page repeated. */
interface Filters { company: string; bu: string; bt: string; warehouse: string }
const NO_FILTERS: Filters = { company: '', bu: '', bt: '', warehouse: '' }
const FILTER_LABEL: Record<keyof Filters, string> = {
  company: 'Company', bu: 'Business unit', bt: 'Business team', warehouse: 'Warehouse',
}
const FILTER_KIND: Record<keyof Filters, InvFilterKind> = {
  company: 'company', bu: 'businessUnit', bt: 'businessTeam', warehouse: 'warehouse',
}

/** What every extract needs to describe itself: the file's reader was not at the screen. */
interface Ctx {
  currency: Currency; viewAs: string | null; search: string; page: number; filters: Filters
  /** The age filter AS APPLIED to this screen: Aging and Lots carry it, Expiry does not. */
  age: InvAgeBand | undefined
  sort: SortState
  onSortChange: (next: SortState) => void
  canSeeCost: boolean
  /** The new permission model answered: a blank figure is withheld, and Partial marks a partly withheld one (M8). */
  v2: boolean
}
const or = (v: string | undefined) => v || undefined

const context = (ctx: Ctx, extra: string[] = []): string[] => [
  isUsd(ctx.currency)
    ? 'Currency: US dollars, converted per lot off each lot’s own currency'
    : isConverted(ctx.currency)
      ? `Currency: ${currencyShort(ctx.currency)}, converted per lot through US dollars`
      : 'Currency: local, as booked — figures from different companies are not comparable',
  ...(Object.keys(ctx.filters) as (keyof Filters)[])
    .filter((k) => ctx.filters[k])
    .map((k) => `${FILTER_LABEL[k]}: ${ctx.filters[k]}`),
  ...(ctx.age ? [`Age: ${AGE_LABEL[ctx.age]} only`] : []),
  ...extra,
  ...(ctx.search ? [`Search: contains "${ctx.search}"`] : []),
  ...(ctx.canSeeCost ? [] : ['Values: not shown — they need the inventory-cost permission']),
]

const pageNote = (ctx: Ctx, rows: number, total: number | undefined) =>
  total && total > rows
    ? `Rows ${(ctx.page - 1) * PAGE_SIZE + 1}–${(ctx.page - 1) * PAGE_SIZE + rows} of ${total.toLocaleString()}. This extract is the page on screen, not the whole result.`
    : undefined

interface XCol<T> { header: string; kind?: CellKind; cell: (r: T) => string | number | null }
/** The columns that hold a figure, which the new model can withhold. */
const FIGURE_KINDS = new Set<CellKind | undefined>(['money', 'qty', 'num2'])

function gridDoc<T>(
  ctx: Ctx, gridId: string, page: string, title: string, subtitle: string, cols: XCol<T>[], rows: T[],
  total: number | undefined, extra: string[] = [], note?: string,
): ExportDoc {
  // The extract follows the grid's own arrangement: see Material Planning.
  const shown = applyLayout(gridId, cols)
  // Under the new model the file says what the screen says (M8): a blank figure
  // is withheld, and one that leaves withheld values out is noted, as a file
  // carries no mark per cell.
  const cell = (c: XCol<T>, r: T) => {
    const v = c.cell(r)
    return ctx.v2 && v === null && FIGURE_KINDS.has(c.kind) ? WITHHELD : v
  }
  const partly = ctx.v2 && rows.some((r) => partialOf(r as { partial?: string | null }).size > 0)
  const pageLine = note ?? pageNote(ctx, rows.length, total)
  return {
    report: 'Inventory Aging', page, title, subtitle,
    context: context(ctx, extra), privileged: ctx.viewAs, currency: ctx.currency,
    tables: [{
      note: partly ? [pageLine, PARTIAL_NOTE].filter(Boolean).join(' ') : pageLine,
      columns: shown.map((c) => ({ header: c.header, kind: c.kind })),
      rows: rows.map((r) => ({ cells: shown.map((c) => cell(c, r)) })),
    }],
  }
}

/** One picker of the standing bar: an eyebrow, a SearchSelect, an "All" that clears it. */
function Pick({ label, value, onChange, options, measureKey }: {
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]
  measureKey?: string
}) {
  return (
    <div className="flex min-w-[150px] flex-col gap-1 max-w-[240px]">
      <Explain surface="filters" measureKey={measureKey ?? ''} className="eyebrow">
        {label}
      </Explain>
      <SearchSelect label={label} value={value} onChange={onChange} options={[{ value: '', label: 'All' }, ...options]} />
    </div>
  )
}

export function InventoryAgingPage({ api, viewAs, roles = [] }: {
  api: Api
  viewAs: string | null
  /** For developer mode. Affordance only — the API refuses the panel to anyone who is not a System Admin. */
  roles?: string[]
}) {
  const [tab, setTab] = useState<TabId>('aging')
  /* Remembered across every report since 4 Oct 2026: rememberedCurrency.ts. */
  const [currency, chooseCurrency, setCurrency] = useReportCurrency(api, viewAs)
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const [search, setSearch] = useState('')
  const [groupBy, setGroupBy] = useState<InvGroupBy>('company')
  /* THE DRILL-DOWN FIRST (owner, 5 Oct 2026): Power BI V1's matrix, opened
     Company -> Unit -> Team -> Product -> Lot. The list is one switch away,
     and still the only way to rank every product across companies. */
  const [agingView, setAgingView] = useState<AgingView>('drill')
  const [measure, setMeasure] = useState<Measure>('value')
  /* Within 90 days first: Power BI V1's "Near To Expire (90 Days)" and V2's
     first expiry page — the list somebody can still act on. */
  const [expiryWindow, setExpiryWindow] = useState<InvWindow>('within90')
  /* THE AGE FILTER (5 Oct 2026): Power BI's "Aging Period" slicer, which sat on
     the By Company / By BU / By BT pages and so narrows Aging and Lots — never
     Expiry, where Power BI did not offer it. '' is every age. */
  const [ageBand, setAgeBand] = useState<InvAgeBand | ''>('')
  const ageOn: InvAgeBand | undefined = tab !== 'expiry' && ageBand ? ageBand : undefined
  /* THE COLUMN THE READER CLICKED, sent to the SERVER: every grid here is paged
     at 100 rows, so a browser sort would reorder the page and look exactly like
     a sorted report. Cleared on a tab change — see Material Planning. */
  const [gridSort, setGridSort] = useState<SortState>({ key: null, desc: true })
  const [page, setPage] = useState(1)

  const [options, setOptions] = useState<InvFilterRow[] | null>(null)
  const [overview, setOverview] = useState<InvOverview | null>(null)
  /** Which model answered the tiles, which every screen reads (M8). */
  const [v2, setV2] = useState(false)
  const [matrix, setMatrix] = useState<InvMatrixRow[] | null>(null)
  const [lots, setLots] = useState<InvLotRow[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)
  const [retry, setRetry] = useState(0)

  const fail = useCallback((e: unknown) => {
    setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not load Inventory Aging.' })
  }, [])

  /* ASKED, NOT INFERRED: the procedure computes the mask, so it says whether
     money is visible. Assumed visible until the first answer lands, so a cost
     holder's columns do not jump in a moment after the grid. */
  const canSeeCost = overview?.canSeeCost ?? true
  /* A reader without cost has one measure, whatever a saved view or the last
     reader of this tab chose. */
  const shownMeasure: Measure = canSeeCost ? measure : 'quantity'

  const set = (k: keyof Filters) => (v: string) => setFilters((f) => ({ ...f, [k]: v }))
  const opts = useCallback((kind: InvFilterKind) =>
    (options ?? []).filter((o) => o.kind === kind && o.code).map((o) => ({ value: o.code!, label: o.label ?? o.code! })),
  [options])

  // The option lists once per identity; they are scoped to the caller's own rows.
  useEffect(() => {
    let cancelled = false
    api.inventoryAgingFilters()
      .then((r) => { if (!cancelled) setOptions(r) })
      /* A refusal is the report's refusal and belongs on the page; an error
         only empties the pickers — the report itself may still load. */
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof ApiError && e.failure.kind !== 'error') fail(e)
        else setOptions([])
      })
    return () => { cancelled = true }
  }, [api, viewAs, fail, retry])

  // Paging resets whenever the question changes.
  useEffect(() => { setPage(1) }, [tab, search, currency, filters, groupBy, expiryWindow, ageBand, gridSort])

  /* A SORT DOES NOT SURVIVE A TAB CHANGE, a grouping change or a measure
     change: its key names a column of the grid that was showing, and the
     grouping and the measure each change which columns there are. */
  /* THE SAME OBJECT when there is nothing to clear: a fresh `{ key: null }`
     is a new state, which re-runs the read below — and on the drill-down that
     swapped the grid for a spinner and closed every branch the reader had
     opened, just for switching Value to Quantity (found 5 Oct 2026). */
  useEffect(() => {
    setGridSort((s) => (s.key === null ? s : { key: null, desc: true }))
  }, [tab, groupBy, shownMeasure])

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    setBusy(true)
    const base = {
      currency,
      company: or(filters.company), bu: or(filters.bu), bt: or(filters.bt), warehouse: or(filters.warehouse),
      search: search || undefined,
      // The tiles answer the age filter too, as Power BI's cards answered the slicer.
      ageBand: ageOn,
    }
    /* `sortBy: undefined` when nothing is sorted, so the procedure keeps the
       report's own order: the client never names a default. */
    const paged = { page, pageSize: PAGE_SIZE, sortBy: gridSort.key ?? undefined, sortDesc: gridSort.desc }
    const run = async () => {
      try {
        /* THE TILES AND THE GRID ANSWER THE SAME SELECTION, search and
           warehouse included, so they can never disagree on screen. */
        if (tab === 'aging' && agingView === 'drill') {
          // The drill-down reads its own branches; the page reads the tiles.
          const o = await api.inventoryAgingOverview(base)
          if (!cancelled) { setOverview(o.data); setV2(o.vis !== null) }
        } else if (tab === 'aging') {
          const [o, m] = await Promise.all([
            api.inventoryAgingOverview(base),
            api.inventoryAgingMatrix({ ...base, ...paged, groupBy }),
          ])
          if (!cancelled) { setOverview(o.data); setV2(o.vis !== null); setMatrix(m.data) }
        } else {
          const [o, l] = await Promise.all([
            api.inventoryAgingOverview(base),
            api.inventoryAgingLots({ ...base, ...paged, window: tab === 'lots' ? 'all' : expiryWindow }),
          ])
          if (!cancelled) { setOverview(o.data); setV2(o.vis !== null); setLots(l.data) }
        }
      } catch (e) {
        if (!cancelled) fail(e)
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    void run()
    return () => { cancelled = true }
  }, [api, tab, agingView, currency, filters, search, groupBy, expiryWindow, ageOn, page, gridSort, viewAs, fail, retry])

  /* The pager belongs to the paged grids. The drill-down loads each branch
     whole, so it has none — and a pager counting the LIST's rows under it
     would describe a result the screen is not showing. */
  const totalRows = tab === 'aging'
    ? (agingView === 'list' ? matrix?.[0]?.totalRows : undefined)
    : lots?.[0]?.totalRows
  const pages = totalRows ? Math.ceil(totalRows / PAGE_SIZE) : 1
  const ctx: Ctx = { currency, viewAs, search, page, filters, age: ageOn, sort: gridSort, onSortChange: setGridSort, canSeeCost, v2 }
  const active = TABS.find((t) => t.id === tab) ?? TABS[0]

  /* ---------------------------------------------------------------------
     SAVED VIEWS. The same adapter as the other three reports — see
     ReceivablesAgingPage for the reasoning behind every rule it follows.
     Declared BELOW the effect that clears the sort on a tab change, so a
     view's sort is applied after the clear rather than wiped by it.
     --------------------------------------------------------------------- */
  const { views, save, remove, rename, setDefault } = useSavedViews(api, 'inventory-aging', viewAs)
  const [activeView, setActiveView] = useState<number | null>(null)
  const [adjusted, setAdjusted] = useState<DropReport>(NOTHING_DROPPED)
  const appliedDefault = useRef<Set<string>>(new Set())

  const [rates, setRates] = useState<CurrencyRate[] | null>(null)
  useEffect(() => {
    let cancelled = false
    api.currencyRates().then((r) => !cancelled && setRates(r)).catch(() => !cancelled && setRates([]))
    return () => { cancelled = true }
  }, [api])

  const tabViews = useMemo(() => views.filter((v) => v.pageSlug === tab), [views, tab])

  const capture = useCallback((): ViewPayload => {
    const grids: Record<string, { order?: string[]; hidden?: string[]; sort?: SortState }> = {}
    for (const gridId of gridsOnTab('inventory-aging', tab)) {
      const snap = snapshot(gridId)
      /* The drill-down keeps its own sort (it sorts each branch in the
         browser); the paged grids share the page's server sort. */
      const sort = gridId === DRILL_GRID ? snapshotSort(gridId) : gridSort.key ? gridSort : null
      if (!snap && !sort) continue
      grids[gridId] = {
        ...(snap ? { order: snap.order, hidden: snap.hidden } : {}),
        ...(sort ? { sort } : {}),
      }
    }
    return {
      v: VIEW_SCHEMA,
      currency: toParam(currency) ?? 'local',
      filters,
      search,
      groupBy,
      agingView,
      measure,
      window: expiryWindow,
      ageBand,
      grids,
    }
  }, [tab, currency, filters, search, groupBy, agingView, measure, expiryWindow, ageBand, gridSort])

  const apply = useCallback(
    (payload: ViewPayload): DropReport => {
      const dropped: string[] = []
      const offered = (kind: InvFilterKind, code: string) =>
        !options || options.some((o) => o.kind === kind && o.code === code)

      /* A value the reader can no longer be offered is left out rather than
         applied: a picker naming a warehouse their stock does not contain is
         the screen lying about what it shows. */
      const stored = (payload.filters ?? {}) as Partial<Record<keyof Filters, unknown>>
      const next: Filters = { ...NO_FILTERS }
      for (const [key, kind] of Object.entries(FILTER_KIND) as [keyof Filters, InvFilterKind][]) {
        const v = stored[key]
        if (typeof v !== 'string' || v === '') continue
        if (!offered(kind, v)) {
          dropped.push(FILTER_LABEL[key].toLowerCase())
          continue
        }
        next[key] = v
      }
      setFilters(next)

      setSearch(typeof payload.search === 'string' ? payload.search.slice(0, 120) : '')
      if (typeof payload.groupBy === 'string' && payload.groupBy in GROUP_LABEL) setGroupBy(payload.groupBy as InvGroupBy)
      else setGroupBy('company')
      // A view saved before the drill-down existed was a list, and opens as one.
      setAgingView(payload.agingView === 'drill' ? 'drill' : payload.agingView === 'list' || payload.groupBy ? 'list' : 'drill')
      setMeasure(payload.measure === 'quantity' ? 'quantity' : 'value')
      if (typeof payload.window === 'string' && payload.window in WINDOW_LABEL) setExpiryWindow(payload.window as InvWindow)
      else setExpiryWindow('within90')
      // A view saved before the age filter existed is every age.
      setAgeBand(typeof payload.ageBand === 'string' && payload.ageBand in AGE_LABEL ? payload.ageBand as InvAgeBand : '')

      /* CURRENCY: the CODE is stored, never the rate. */
      const code = typeof payload.currency === 'string' ? payload.currency : 'local'
      if (code === 'local') setCurrency(LOCAL)
      else if (code === 'usd') setCurrency(USD)
      else if (rates) {
        const live = rates.find((r) => r.code.toLowerCase() === code.toLowerCase())
        if (live) setCurrency(inCurrency(live.code, live.ratePerUsd))
        else dropped.push(`the ${code.toUpperCase()} currency`)
      }

      const grids = (payload.grids ?? {}) as Record<string, { order?: string[]; hidden?: string[]; sort?: { key?: unknown; desc?: unknown } }>
      for (const [gridId, g] of Object.entries(grids)) {
        const current = snapshot(gridId)
        if (!current || !Array.isArray(g.order)) continue
        restore(gridId, {
          ...current,
          order: g.order.filter((k) => current.order.includes(k)),
          hidden: (g.hidden ?? []).filter((k) => current.order.includes(k)),
        })
      }

      /* The sort of this tab's paged grid, or its natural order. A key the
         server does not know is clamped there; a masked one is refused and
         recorded. The drill-down's sort goes back through its own mirror,
         which validates the key against its columns. */
      const listGrid = tab === 'aging' ? LIST_GRID : gridsOnTab('inventory-aging', tab)[0] ?? ''
      const s = grids[listGrid]?.sort
      setGridSort(
        s && typeof s.key === 'string' && s.key ? { key: s.key, desc: s.desc !== false } : { key: null, desc: true },
      )
      const ds = grids[DRILL_GRID]?.sort
      if (tab === 'aging' && ds && typeof ds.key === 'string' && ds.key) restoreSort(DRILL_GRID, { key: ds.key, desc: ds.desc !== false })

      return { dropped }
    },
    [options, rates, tab, setCurrency],
  )

  const viewDirty = useViewDirty(views, activeView, capture)

  const applyStored = useCallback(
    (v: StoredReaderView) => {
      const payload = parseView(v.view, v.schemaVersion)
      if (!payload) return
      setAdjusted(apply(payload))
      setActiveView(v.viewId)
    },
    [apply],
  )

  /* THE DEFAULT, ONCE PER TAB, AND ONLY AFTER THE PICKERS HAVE LANDED. */
  useEffect(() => {
    if (!options || !rates) return
    if (appliedDefault.current.has(tab)) return
    const def = views.find((v) => v.pageSlug === tab && v.isDefault)
    appliedDefault.current.add(tab)
    if (def) applyStored(def)
  }, [options, rates, views, tab, applyStored])

  // A different pair of eyes means different views and a different book.
  useEffect(() => {
    appliedDefault.current = new Set()
    setActiveView(null)
    setAdjusted(NOTHING_DROPPED)
  }, [viewAs])

  return (
    <NewModel.Provider value={v2}>
    <DevModeProvider
      api={api}
      roles={roles}
      report="inventory-aging"
      request={{
        Screen: tab,
        Currency: currencyShort(currency),
        ...(filters.company ? { Company: filters.company } : {}),
        ...(filters.bu ? { 'Business unit': filters.bu } : {}),
        ...(filters.bt ? { 'Business team': filters.bt } : {}),
        ...(filters.warehouse ? { Warehouse: filters.warehouse } : {}),
        ...(ageOn ? { Age: AGE_LABEL[ageOn] } : {}),
        ...(search ? { Search: search } : {}),
      }}
    >
    <div className="stack-tight flex h-full flex-col gap-4">
      <ReportHeader
        report="Inventory Aging"
        screen={active.label}
        what="Stock by how long it has been held and when it expires."
        viewAs={viewAs}
        view={
          <SavedViewsMenu
            views={tabViews}
            activeId={activeView}
            acting={viewAs}
            dirty={viewDirty}
            onApply={applyStored}
            onRename={rename}
            onSave={async (name, makeDefault) => {
              setActiveView(await save(tab, name, capture(), makeDefault))
            }}
            onDelete={(id) => {
              void remove(id)
              if (id === activeView) setActiveView(null)
            }}
            onSetDefault={(id, on) => void setDefault(id, on)}
          />
        }
        right={
          <>
            <SearchInput value={search} onChange={setSearch} placeholder="Item, product or lot…" label="Search" />
            <CurrencyControl api={api} label="Currency" value={currency} onChange={chooseCurrency} />
          </>
        }
      />

      <PageTabs
        tabs={TABS}
        value={tab}
        onChange={setTab}
        label="Inventory Aging pages"
        surface="filters"
        measureKey="view"
      />

      <ViewAdjustedNotice report={adjusted} onDismiss={() => setAdjusted(NOTHING_DROPPED)} />

      {/* THE STANDING FILTER BAR: the four slicers every Power BI page repeated,
          once, following the reader from screen to screen. The Power BI "Aging
          Period" slicer is the Age picker, on the two screens whose Power BI
          pages carried it; its "Expiry Period" slicer is the Expiry window. */}
      <div className="flex flex-wrap items-end gap-3">
        <Pick measureKey="company" label="Company" value={filters.company} onChange={set('company')} options={opts('company')} />
        <Pick measureKey="bu" label="Business unit" value={filters.bu} onChange={set('bu')} options={opts('businessUnit')} />
        <Pick measureKey="bt" label="Business team" value={filters.bt} onChange={set('bt')} options={opts('businessTeam')} />
        <Pick measureKey="warehouse" label="Warehouse" value={filters.warehouse} onChange={set('warehouse')} options={opts('warehouse')} />
        {tab !== 'expiry' ? (
          <Pick
            measureKey="ageBand" label="Age" value={ageBand}
            onChange={(v) => setAgeBand(v as InvAgeBand | '')}
            options={BUCKETS.map((b) => ({ value: b.band, label: AGE_LABEL[b.band] }))}
          />
        ) : null}
        {tab === 'aging' ? (
          <>
            <div className="flex flex-col gap-1">
              <Explain surface="filters" measureKey="agingView" className="eyebrow">
                View
              </Explain>
              <Segmented
                label="View"
                options={[
                  { value: 'drill', label: 'Drill down', title: 'Company, then business unit, team, product and lot, one level at a time' },
                  { value: 'list', label: 'List', title: 'One flat list at the grouping you choose, ranked across every company' },
                ]}
                value={agingView}
                onChange={(v) => setAgingView(v as AgingView)}
              />
            </div>
            {agingView === 'list' ? (
              <div className="flex flex-col gap-1">
                <Explain surface="filters" measureKey="groupBy" className="eyebrow">
                  Group by
                </Explain>
                <Segmented
                  label="Group by"
                  options={(Object.keys(GROUP_LABEL) as InvGroupBy[]).map((g) => ({ value: g, label: GROUP_LABEL[g] }))}
                  value={groupBy}
                  onChange={setGroupBy}
                />
              </div>
            ) : null}
            {canSeeCost ? (
              <div className="flex flex-col gap-1">
                <Explain surface="filters" measureKey="measure" className="eyebrow">
                  Show
                </Explain>
                <Segmented
                  label="Show"
                  options={[
                    { value: 'value', label: 'Value', title: 'The buckets in money: what the stock cost' },
                    { value: 'quantity', label: 'Quantity', title: 'The buckets in units, each lot in its own unit of measure' },
                  ]}
                  value={measure}
                  onChange={(m) => setMeasure(m as Measure)}
                />
              </div>
            ) : null}
          </>
        ) : null}
        {tab === 'expiry' ? (
          <div className="flex flex-col gap-1">
            <Explain surface="filters" measureKey="window" className="eyebrow">
              Expiry
            </Explain>
            <Segmented
              label="Expiry"
              options={(Object.keys(WINDOW_LABEL) as InvWindow[]).map((w) => ({ value: w, label: WINDOW_LABEL[w], title: WINDOW_TITLE[w] }))}
              value={expiryWindow}
              onChange={setExpiryWindow}
            />
          </div>
        ) : null}
        {Object.values(filters).some(Boolean) || ageOn ? (
          <Button onClick={() => { setFilters(NO_FILTERS); setAgeBand('') }}>Clear filters</Button>
        ) : null}
      </div>

      {!isConverted(currency) && (overview?.currencyCount ?? 0) > 1 && canSeeCost ? (
        <p className="text-(length:--text-small) text-(--color-ink-3)">
          Local money adds different currencies together. Quantities are unaffected; switch to USD to compare values across companies.
        </p>
      ) : null}

      {failure ? <FailureState failure={failure} onRetry={() => setRetry((n) => n + 1)} />
        : busy ? <Loading label="Loading Inventory Aging…" /> : (
      <>
      {tab === 'aging' && agingView === 'drill' ? (
        <AgingDrillTab api={api} cards={overview} measure={shownMeasure} ctx={ctx} />
      ) : null}
      {tab === 'aging' && agingView === 'list' ? (
        <AgingTab rows={matrix} cards={overview} groupBy={groupBy} measure={shownMeasure} ctx={ctx} />
      ) : null}
      {tab === 'expiry' ? <ExpiryTab rows={lots} cards={overview} win={expiryWindow} ctx={ctx} /> : null}
      {tab === 'lots' ? <LotsTab rows={lots} cards={overview} ctx={ctx} /> : null}
      </>
      )}

      {!failure && totalRows !== undefined && totalRows > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-(length:--text-small) text-(--color-ink-3)">
          <span>
            {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, totalRows).toLocaleString()} of {totalRows.toLocaleString()}
          </span>
          <span className="flex items-center gap-2">
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
            <span>Page {page} of {pages}</span>
            <Button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>Next</Button>
          </span>
        </div>
      ) : null}
    </div>
    </DevModeProvider>
    </NewModel.Provider>
  )
}

/* ------------------------------------------------------------------- aging */

/**
 * THE AGING MATRIX: Power BI's By Company / By BU / By BT pages, the V1 matrix
 * and the tree, as one grid whose grouping is a picker — so a reader moves
 * between them without losing their filters.
 *
 * The identity columns are the grouping's own, drawn only where they can hold a
 * value: a company row adds up hundreds of items, so it has no item, no lot and
 * no warehouse to show (the layout of a column not drawn waits, dormant, for the
 * grouping that has it). Days appear on lot rows only; every group shows its
 * oldest arrival and earliest expiry instead, which is what the summed days in
 * Power BI were trying and failing to say.
 */
function AgingTab({ rows, cards, groupBy, measure, ctx }: {
  rows: InvMatrixRow[] | null; cards: InvOverview | null; groupBy: InvGroupBy; measure: Measure; ctx: Ctx
}) {
  const atItem = groupBy === 'product' || groupBy === 'productwh' || groupBy === 'lot'
  const atLot = groupBy === 'lot'
  /* A product by warehouse names its warehouse, as a lot does; it is still a
     group of lots, so it keeps its lot count and no days of its own. */
  const atWarehouse = groupBy === 'productwh' || atLot
  const columns = useMemo<Column<InvMatrixRow>[]>(() => {
    const noUnit = <span className="italic text-(--color-ink-3)" title="The item carries no business unit in D365">No business unit</span>
    const pick = (band: InvAgeBand) => BUCKET_FIELD[band][measure === 'value' ? 'v' : 'q']
    return withAnswer(ctx.v2, measure === 'value', [
      { key: 'company', sortKey: 'companyname', header: 'Company', render: (r) => r.companyName ?? r.company, clamp: 30 },
      ...(groupBy === 'bu' || groupBy === 'bt' ? [{
        key: 'bu', sortKey: 'businessunit', header: 'Business Unit',
        render: (r: InvMatrixRow) => r.businessUnit ?? noUnit,
      }] : []),
      ...(groupBy === 'bt' ? [{ key: 'bt', sortKey: 'businessteam', header: 'Business Team', render: (r: InvMatrixRow) => r.businessTeam }] : []),
      ...(atItem ? [
        { key: 'item', sortKey: 'itemnumber', header: 'Item Number', render: (r: InvMatrixRow) => r.itemNumber },
        { key: 'product', sortKey: 'productname', header: 'Product', render: (r: InvMatrixRow) => r.productName, clamp: 30 },
      ] : []),
      ...(atLot ? [{ key: 'lot', sortKey: 'lot', header: 'Lot', render: (r: InvMatrixRow) => r.lot }] : []),
      ...(atWarehouse ? [{ key: 'warehouse', sortKey: 'warehouse', header: 'Warehouse', render: (r: InvMatrixRow) => r.warehouse }] : []),
      /* After the codes that say WHERE, so they stay together at the left; Power
         BI V1's product page showed it beside the name. */
      ...(atItem ? [{ key: 'description', sortKey: 'description', header: 'Description', render: (r: InvMatrixRow) => r.description, clamp: 36 }] : []),
      /* A lot row is one lot, so its count says nothing there. */
      ...(atLot ? [] : [{
        key: 'lots', sortKey: 'lots', header: 'Lots', align: 'right' as const,
        render: (r: InvMatrixRow) => <span className="tabular-nums">{r.lots.toLocaleString()}</span>,
        value: (r: InvMatrixRow) => r.lots, total: true,
      }]),
      ...BUCKETS.map((b) => ({
        key: `b${b.band}`,
        sortKey: String(pick(b.band)).toLowerCase(),
        header: b.header,
        align: 'right' as const,
        render: (r: InvMatrixRow) => bucket(r[pick(b.band)] as number | null, measure),
        value: (r: InvMatrixRow) => (r[pick(b.band)] as number | null) ?? 0,
        total: true,
      })),
      /* THE TOTAL CARRIES THE UNIT OR THE CURRENCY, and the buckets beside it do
         not: ten columns of "kg" would bury the figures they label. */
      measure === 'value'
        ? { key: 'total', sortKey: 'value', header: 'Total Value', align: 'right', render: (r) => cash(r.value, curOf(r.currencyCode, r.currencyCount)), value: (r) => r.value ?? 0, total: true }
        : { key: 'total', sortKey: 'quantity', header: 'Total Quantity', align: 'right', render: (r) => num(r.quantity, dimOf(r.unit, r.unitCount)), value: (r) => r.quantity ?? 0, total: true },
      ...(measure === 'value'
        ? [{ key: 'quantity', sortKey: 'quantity', header: 'Quantity', align: 'right' as const, render: (r: InvMatrixRow) => num(r.quantity, dimOf(r.unit, r.unitCount)) }]
        : ctx.canSeeCost
          ? [{ key: 'value', sortKey: 'value', header: 'Value', align: 'right' as const, render: (r: InvMatrixRow) => cash(r.value, curOf(r.currencyCode, r.currencyCount)) }]
          : []),
      ...(ctx.canSeeCost ? [{
        key: 'unitCost', sortKey: 'unitcost', header: 'Unit Cost', align: 'right' as const,
        // Under the new model a blank price is withheld unless the group holds several
        // units or no quantity to divide by.
        render: (r: InvMatrixRow) => (ctx.v2 && r.unitCost === null && r.quantity === 0 ? NO_QUANTITY
          : r.unitCost === null && !atItem && (!ctx.v2 || (r.unitCount ?? 0) > 1)
          ? <span className="text-(--color-ink-4)" title="Several units of measure in this group, so no single price">–</span>
          : price(r.unitCost, curOf(r.currencyCode, r.currencyCount))),
      }] : []),
      { key: 'oldest', sortKey: 'oldestarrival', header: atLot ? 'Arrival Date' : 'Oldest Arrival', render: (r) => date(r.oldestArrival) },
      ...(atLot ? [{ key: 'daysOld', sortKey: 'daysold', header: 'Days Old', align: 'right' as const, render: (r: InvMatrixRow) => daysOld(r.daysOld) }] : []),
      { key: 'earliest', sortKey: 'earliestexpiry', header: atLot ? 'Expiry Date' : 'Earliest Expiry', render: (r) => date(r.earliestExpiry) },
      ...(atLot ? [{ key: 'daysToExpire', sortKey: 'daystoexpire', header: 'Days to Expire', align: 'right' as const, render: (r: InvMatrixRow) => toExpire(r.daysToExpire, r.earliestExpiry) }] : []),
      ...(atItem ? [
        { key: 'buItem', sortKey: 'businessunit', header: 'Business Unit', render: (r: InvMatrixRow) => r.businessUnit ?? noUnit },
        { key: 'btItem', sortKey: 'businessteam', header: 'Business Team', render: (r: InvMatrixRow) => r.businessTeam },
      ] : []),
      { key: 'unit', sortKey: 'unit', header: 'Unit', render: (r) => dimOf(r.unit, r.unitCount)?.text ?? null },
      ...(ctx.canSeeCost ? [{ key: 'currency', sortKey: 'currencycode', header: 'Currency', render: (r: InvMatrixRow) => (r.currencyCode ? currencyMark(r.currencyCode) : curOf(null, r.currencyCount)?.text ?? null) }] : []),
    ])
  }, [groupBy, measure, atItem, atLot, atWarehouse, ctx.canSeeCost, ctx.v2])

  const buildDoc = () => {
    if (!rows?.length) return null
    const pick = (band: InvAgeBand) => BUCKET_FIELD[band][measure === 'value' ? 'v' : 'q']
    const kind: CellKind = measure === 'value' ? 'money' : 'qty'
    return gridDoc<InvMatrixRow>(ctx, 'inv-aging', 'Aging', `Inventory aging by ${GROUP_LABEL[groupBy].toLowerCase()}`,
      measure === 'value' ? 'Stock value by how long it has been held.' : 'Stock quantity by how long it has been held.', [
      { header: 'Company', cell: (r) => r.companyName ?? r.company },
      ...(groupBy === 'bu' || groupBy === 'bt' ? [{ header: 'Business Unit', cell: (r: InvMatrixRow) => r.businessUnit ?? 'No business unit' }] : []),
      ...(groupBy === 'bt' ? [{ header: 'Business Team', cell: (r: InvMatrixRow) => r.businessTeam }] : []),
      ...(atItem ? [
        { header: 'Item Number', cell: (r: InvMatrixRow) => r.itemNumber },
        { header: 'Product', cell: (r: InvMatrixRow) => r.productName },
      ] : []),
      ...(atLot ? [{ header: 'Lot', cell: (r: InvMatrixRow) => r.lot }] : []),
      ...(atWarehouse ? [{ header: 'Warehouse', cell: (r: InvMatrixRow) => r.warehouse }] : []),
      ...(atItem ? [{ header: 'Description', cell: (r: InvMatrixRow) => r.description }] : []),
      ...(atLot ? [] : [{ header: 'Lots', kind: 'int' as CellKind, cell: (r: InvMatrixRow) => r.lots }]),
      ...BUCKETS.map((b) => ({ header: b.header, kind, cell: (r: InvMatrixRow) => r[pick(b.band)] as number | null })),
      measure === 'value'
        ? { header: 'Total Value', kind: 'money', cell: (r) => r.value }
        : { header: 'Total Quantity', kind: 'qty', cell: (r) => r.quantity },
      ...(measure === 'value' ? [{ header: 'Quantity', kind: 'qty' as CellKind, cell: (r: InvMatrixRow) => r.quantity }]
        : ctx.canSeeCost ? [{ header: 'Value', kind: 'money' as CellKind, cell: (r: InvMatrixRow) => r.value }] : []),
      ...(ctx.canSeeCost ? [{ header: 'Unit Cost', kind: 'num2' as CellKind, cell: (r: InvMatrixRow) => r.unitCost }] : []),
      { header: atLot ? 'Arrival Date' : 'Oldest Arrival', cell: (r) => dateS(r.oldestArrival) },
      ...(atLot ? [{ header: 'Days Old', kind: 'int' as CellKind, cell: (r: InvMatrixRow) => r.daysOld }] : []),
      { header: atLot ? 'Expiry Date' : 'Earliest Expiry', cell: (r) => dateS(r.earliestExpiry) },
      ...(atLot ? [{ header: 'Days to Expire', kind: 'int' as CellKind, cell: (r: InvMatrixRow) => (isPlaceholder(r.earliestExpiry) ? null : r.daysToExpire) }] : []),
      ...(atItem ? [
        { header: 'Business Unit', cell: (r: InvMatrixRow) => r.businessUnit },
        { header: 'Business Team', cell: (r: InvMatrixRow) => r.businessTeam },
      ] : []),
      { header: 'Unit', cell: (r) => dimOf(r.unit, r.unitCount)?.text ?? null },
      ...(ctx.canSeeCost ? [{ header: 'Currency', cell: (r: InvMatrixRow) => curOf(r.currencyCode, r.currencyCount)?.text ?? null }] : []),
    ], rows, rows[0]?.totalRows,
    [`Grouped by: ${GROUP_LABEL[groupBy]}`, `Buckets in: ${measure === 'value' ? 'value' : 'quantity'}`],
    'Age counts from the arrival date. Drop-ship stock and stock with no known arrival are in their own columns, so the buckets add up to the total.')
  }

  return (
    <>
      <AgingTiles cards={cards} ctx={ctx} />

      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Inventory Aging', 'Aging']}
        title={`Stock ${measure === 'value' ? 'value' : 'quantity'} by age, by ${GROUP_LABEL[groupBy].toLowerCase()}`}
        subtitle="Age counts from the arrival date. Drop-ship stock and stock with no known arrival have their own columns, so the buckets add up to the total."
        right={<ExportButtons build={buildDoc} />}
      >
        {!rows ? <Loading label="Loading stock…" />
          : rows.length === 0 ? <EmptyState title="No stock to show" detail="No lot matches this filter within what you are permitted to see." />
          : <DataGrid
              surface="aging" gridId="inv-aging" columns={columns} rows={rows}
              {...(measure === 'quantity' ? totalsOf(rows.map((r) => dimOf(r.unit, r.unitCount)?.text)) : { totalsLabel: 'This page' })}
              totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill
            />}
      </ExpandableCard>
    </>
  )
}

/** The Aging tiles, shared by the drill-down and the list: one selection, one strip. */
function AgingTiles({ cards, ctx }: { cards: InvOverview | null; ctx: Ctx }) {
  const u = dimOf(cards?.unit, cards?.unitCount)
  const c = curOf(cards?.currencyCode, cards?.currencyCount)
  const money_ = ctx.canSeeCost
  const p = partialOf(cards)
  return (
    <CollapsibleStats storageKey="stats:inventory-aging:aging">
      <div className="stat-strip">
        {money_ ? (
          <StatTile surface="aging" measureKey="value" label="Stock value" value={partS(cashS(cards?.value, ctx.v2), p.has('value'))} fullValue={partF(fullCash(cards?.value, ctx.v2), p.has('value'))} secondary={withCur(`${(cards?.lots ?? 0).toLocaleString()} lots`, c)} tone="accent" dense />
        ) : null}
        <StatTile surface="aging" measureKey="quantity" label="Quantity" value={partS(numS(cards?.quantity, ctx.v2), p.has('quantity'))} fullValue={partF(fullQty(cards?.quantity, ctx.v2), p.has('quantity'))} secondary={withDim(money_ ? undefined : `${(cards?.lots ?? 0).toLocaleString()} lots`, u)} tone={money_ ? undefined : 'accent'} dense />
        {([180, 240, 365] as const).map((n) => {
          const v = n === 180 ? cards?.valueOver180 : n === 240 ? cards?.valueOver240 : cards?.valueOver365
          const q = n === 180 ? cards?.qtyOver180 : n === 240 ? cards?.qtyOver240 : cards?.qtyOver365
          return money_ ? (
            <StatTile key={n} surface="aging" measureKey={`over${n}`} label={`Over ${n} days`} value={partS(cashS(v, ctx.v2), p.has(`valueOver${n}`))} fullValue={partF(fullCash(v, ctx.v2), p.has(`valueOver${n}`))} secondary={withCur(share(v, cards?.value), c)} tone={n === 365 && v ? 'bad' : undefined} dense />
          ) : (
            <StatTile key={n} surface="aging" measureKey={`over${n}`} label={`Over ${n} days`} value={partS(numS(q, ctx.v2), p.has(`qtyOver${n}`))} fullValue={partF(fullQty(q, ctx.v2), p.has(`qtyOver${n}`))} secondary={withDim(share(q, cards?.quantity), u)} tone={n === 365 && q ? 'bad' : undefined} dense />
          )
        })}
        <StatTile
          surface="aging" measureKey="drop" label="Drop-ship"
          value={money_ ? partS(cashS(cards?.valueDrop, ctx.v2), p.has('valueDrop')) : partS(numS(cards?.qtyDrop, ctx.v2), p.has('qtyDrop'))}
          fullValue={money_ ? partF(fullCash(cards?.valueDrop, ctx.v2), p.has('valueDrop')) : partF(fullQty(cards?.qtyDrop, ctx.v2), p.has('qtyDrop'))}
          secondary="in no age bucket" dense
        />
        <StatTile surface="aging" measureKey="unknownAge" label="Arrival unknown" value={(cards?.unknownAgeLots ?? 0).toLocaleString()} secondary="lots with no arrival date, or one ahead" dense />
      </div>
    </CollapsibleStats>
  )
}

/* ------------------------------------------------------------- aging drill */

/**
 * THE DRILL-DOWN (owner, 5 Oct 2026): Power BI V1's "Inventory-BU" matrix —
 * Company, then Business unit, Business team and Product — carried down to
 * each lot and its warehouse. One row column, indented per level, with the
 * age buckets across, exactly the Receivables matrix's shape.
 *
 * EACH LEVEL IS THE LIST'S OWN QUERY, one grouping finer and narrowed to the
 * branch opened (040's @Path parameters), so a branch's rows always add up to
 * the row it opened from, under the same masks and the same scope.
 *
 * A BRANCH LOADS WHOLE and sorts in the browser, as the Receivables matrix
 * does. That is honest only because the largest branch is small: measured on
 * the clone, 5 Oct 2026, at most 296 products in a team, 91 lots in a product,
 * 10 units in a company and 7 teams in a unit. A branch that ever outgrows the
 * 500 a read returns says so in its own row rather than sorting a part.
 *
 * THE BLANK MEMBER OPENS. "No business unit" is 1,243 lots on the clone; it
 * carries its own flag in the path (unitNone), so opening it can never look
 * like "no unit chosen yet" and loop back to the companies.
 */
/** Every row a branch can return. Above this the branch says it is partial. */
const BRANCH_MAX = 500
const DRILL_INDENT_PX = 20

/** Which level a path's CHILDREN are. */
function levelOf(p: InvDrillPath): InvGroupBy {
  if (!p.company) return 'company'
  if (p.unit === undefined && !p.unitNone) return 'bu'
  if (p.team === undefined && !p.teamNone) return 'bt'
  if (!p.item) return 'product'
  return 'lot'
}

/** The path that addresses this node's children; null for a lot, which is a leaf. */
function childOf(parent: InvDrillPath, level: InvGroupBy, n: InvMatrixRow): InvDrillPath | null {
  switch (level) {
    case 'company': return n.company ? { company: n.company } : null
    case 'bu': return n.businessUnit ? { ...parent, unit: n.businessUnit } : { ...parent, unitNone: true }
    case 'bt': return n.businessTeam ? { ...parent, team: n.businessTeam } : { ...parent, teamNone: true }
    case 'product': return n.itemNumber ? { ...parent, item: n.itemNumber } : null
    default: return null
  }
}

/* U+001F between the parts, escaped so it is visible: these are live codes,
   and two branches sharing an id would draw one's children under the other. */
const pathKey = (p: InvDrillPath) =>
  [p.company ?? '', p.unitNone ? '\u0000none' : p.unit ?? '', p.teamNone ? '\u0000none' : p.team ?? '', p.item ?? ''].join('\u001f')

/** What the row column says at each level. */
function nodeLabel(level: InvGroupBy, n: InvMatrixRow): ReactNode {
  const quiet = (s: string, title: string) => <span className="font-normal italic text-(--color-ink-3)" title={title}>{s}</span>
  switch (level) {
    case 'company': return n.companyName ?? n.company
    case 'bu': return n.businessUnit ?? quiet('No business unit', 'Items with no business unit in D365. Readers limited to named units or teams cannot see them.')
    case 'bt': return n.businessTeam ?? quiet('No business team', 'Items with no business team in D365.')
    case 'product': return <>{n.productName ?? n.itemNumber}{n.productName ? <span className="ml-2 font-normal text-(--color-ink-3)">{n.itemNumber}</span> : null}</>
    default: return <>{n.lot}<span className="ml-2 font-normal text-(--color-ink-3)">{n.warehouse}</span></>
  }
}
const nodeText = (level: InvGroupBy, n: InvMatrixRow): string =>
  level === 'company' ? n.companyName ?? n.company ?? ''
    : level === 'bu' ? n.businessUnit ?? 'No business unit'
    : level === 'bt' ? n.businessTeam ?? 'No business team'
    : level === 'product' ? `${n.productName ?? ''} (${n.itemNumber ?? ''})`
    : `${n.lot ?? ''} · ${n.warehouse ?? ''}`

interface DrillColumn {
  key: string
  header: string
  title?: string
  /** The figure this column shows and sorts by. */
  pick: (n: InvMatrixRow) => number | string | null
  render: (n: InvMatrixRow, level: InvGroupBy) => ReactNode
  kind: CellKind
  /** Summed into the Total row (money and counts; quantities only when one unit). */
  total?: boolean
}

interface DrillRow { node: InvMatrixRow; level: InvGroupBy; path: InvDrillPath; child: InvDrillPath | null; depth: number; key: string }

function AgingDrillTab({ api, cards, measure, ctx }: {
  api: Api; cards: InvOverview | null; measure: Measure; ctx: Ctx
}) {
  /** Children by branch key. Absent = never fetched. */
  const [children, setChildren] = useState<Map<string, InvMatrixRow[]>>(new Map())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState<Set<string>>(new Set())
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /** A branch that failed, kept apart from `children` so a failure is never cached as an empty branch. */
  const [branchFailure, setBranchFailure] = useState<Map<string, ApiFailure>>(new Map())
  const [sort, setSort] = useState<SortState>({ key: 'total', desc: true })
  const [nonce, setNonce] = useState(0)
  const memberRef = useRef<HTMLTableCellElement | null>(null)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)

  const value = measure === 'value'
  const columns = useMemo<DrillColumn[]>(() => {
    const field = (band: InvAgeBand) => BUCKET_FIELD[band][value ? 'v' : 'q']
    const cols: DrillColumn[] = [
      { key: 'lots', header: 'Lots', kind: 'int', pick: (n) => n.lots, render: (n) => <span className="tabular-nums">{n.lots.toLocaleString()}</span>, total: true },
      ...BUCKETS.map<DrillColumn>((b) => ({
        key: `b${b.band}`, header: b.header, title: b.title, kind: value ? 'money' : 'qty', total: true,
        pick: (n) => n[field(b.band)] as number | null,
        render: (n) => bucket(n[field(b.band)] as number | null, measure),
      })),
      value
        ? { key: 'total', header: 'Total Value', kind: 'money', total: true, pick: (n) => n.value, render: (n) => cash(n.value, curOf(n.currencyCode, n.currencyCount)) }
        : { key: 'total', header: 'Total Quantity', kind: 'qty', total: true, pick: (n) => n.quantity, render: (n) => num(n.quantity, dimOf(n.unit, n.unitCount)) },
      ...(value
        ? [{ key: 'quantity', header: 'Quantity', kind: 'qty' as CellKind, pick: (n: InvMatrixRow) => n.quantity, render: (n: InvMatrixRow) => num(n.quantity, dimOf(n.unit, n.unitCount)) }]
        : ctx.canSeeCost
          ? [{ key: 'value', header: 'Value', kind: 'money' as CellKind, total: true, pick: (n: InvMatrixRow) => n.value, render: (n: InvMatrixRow) => cash(n.value, curOf(n.currencyCode, n.currencyCount)) }]
          : []),
      ...(ctx.canSeeCost ? [{
        key: 'unitCost', header: 'Unit Cost', kind: 'num2' as CellKind,
        pick: (n: InvMatrixRow) => n.unitCost,
        render: (n: InvMatrixRow, level: InvGroupBy) => (ctx.v2 && n.unitCost === null && n.quantity === 0 ? NO_QUANTITY
          : n.unitCost === null && level !== 'product' && level !== 'lot' && (!ctx.v2 || (n.unitCount ?? 0) > 1)
          ? <span className="text-(--color-ink-4)" title="Several units of measure in this group, so no single price">–</span>
          : price(n.unitCost, curOf(n.currencyCode, n.currencyCount))),
      }] : []),
      { key: 'oldest', header: 'Oldest Arrival', kind: 'text', pick: (n) => n.oldestArrival, render: (n) => date(n.oldestArrival) },
      { key: 'earliest', header: 'Earliest Expiry', kind: 'text', pick: (n) => n.earliestExpiry, render: (n) => date(n.earliestExpiry) },
      /* A lot's own; blank on every group row, never a sum (B1). */
      { key: 'daysOld', header: 'Days Old', kind: 'int', pick: (n) => n.daysOld, render: (n, level) => (level === 'lot' ? daysOld(n.daysOld) : null) },
      { key: 'daysToExpire', header: 'Days to Expire', kind: 'int', pick: (n) => n.daysToExpire, render: (n, level) => (level === 'lot' ? toExpire(n.daysToExpire, n.earliestExpiry) : null) },
      /* The product's own words, on product and lot rows — the procedure sends
         none above them. Clamped like every long text here, whole on hover. */
      {
        key: 'description', header: 'Description', kind: 'text', pick: (n) => n.description,
        render: (n) => (n.description
          ? <span className="inline-block max-w-[36ch] truncate align-bottom" title={n.description}>{n.description}</span>
          : null),
      },
      { key: 'unit', header: 'Unit', kind: 'text', pick: (n) => dimOf(n.unit, n.unitCount)?.text ?? null, render: (n) => dimOf(n.unit, n.unitCount)?.text ?? null },
      ...(ctx.canSeeCost ? [{
        key: 'currency', header: 'Currency', kind: 'text' as CellKind,
        pick: (n: InvMatrixRow) => curOf(n.currencyCode, n.currencyCount)?.text ?? null,
        render: (n: InvMatrixRow) => (n.currencyCode ? currencyMark(n.currencyCode) : curOf(null, n.currencyCount)?.text ?? null),
      }] : []),
    ]
    // Under the new model a figure that leaves withheld lots out keeps its value and gains the mark (M8).
    return !ctx.v2 ? cols : cols.map((c) => {
      const f = figureOf(c.key, value)
      const render = c.render
      return f ? { ...c, render: (n: InvMatrixRow, level: InvGroupBy) => markPartial(partialOf(n).has(f), render(n, level)) } : c
    })
  }, [value, measure, ctx.canSeeCost, ctx.v2])

  const layout = useGridLayout(DRILL_GRID, columns, memberRef)
  const shown = layout.columns
  /* THE SORT RIDES INSIDE A SAVED VIEW, validated against this grid's own
     columns: a key it does not know falls back to the biggest total first. */
  useSortMirror(DRILL_GRID, sort, (s) => {
    const known = columns.some((c) => c.key === s.key)
    setSort(known ? { key: s.key, desc: s.desc } : { key: 'total', desc: true })
  })

  /* THE GENERATION of the tree: bumped whenever it is discarded, captured by
     every read before it awaits, so a response for an abandoned tree — the
     old currency, the old filter, the old reader — is dropped, not drawn. */
  const gen = useRef(0)
  const rootKey = pathKey({})

  const { currency, filters, search, viewAs, age } = ctx
  const fetchBranch = useCallback(async (path: InvDrillPath) => {
    const id = pathKey(path)
    const g = gen.current
    setLoading((s) => new Set(s).add(id))
    setBranchFailure((m) => { if (!m.has(id)) return m; const next = new Map(m); next.delete(id); return next })
    try {
      const { data: rows } = await api.inventoryAgingMatrix({
        groupBy: levelOf(path), currency,
        company: or(filters.company), bu: or(filters.bu), bt: or(filters.bt), warehouse: or(filters.warehouse),
        search: search || undefined, page: 1, pageSize: BRANCH_MAX,
        pathCompany: path.company, pathUnit: path.unit, pathUnitNone: path.unitNone || undefined,
        pathTeam: path.team, pathTeamNone: path.teamNone || undefined, pathItem: path.item,
        // Every level answers the age filter, so a branch still adds up to its row.
        ageBand: age,
      })
      if (g !== gen.current) return
      setChildren((m) => new Map(m).set(id, rows))
    } catch (e: unknown) {
      if (g !== gen.current) return
      const f = e instanceof ApiError ? e.failure : { kind: 'error' as const, title: 'Could not load this branch.' }
      // Only a failed ROOT replaces the table; a failed branch keeps what is true on screen.
      if (id === rootKey) setFailure(f)
      else {
        setExpanded((s) => { const next = new Set(s); next.delete(id); return next })
        setBranchFailure((m) => new Map(m).set(id, f))
      }
    } finally {
      if (g === gen.current) setLoading((s) => { const next = new Set(s); next.delete(id); return next })
    }
  }, [api, currency, filters, search, age, rootKey])

  // A new currency, filter, age, search or reader changes every number: start again.
  useEffect(() => {
    gen.current += 1
    setChildren(new Map())
    setExpanded(new Set())
    setLoading(new Set())
    setFailure(null)
    setBranchFailure(new Map())
    void fetchBranch({})
  }, [fetchBranch, viewAs, nonce])

  const toggle = useCallback((row: DrillRow) => {
    if (!row.child) return
    const id = pathKey(row.child)
    // A path that did not advance is a leaf, whatever the row claims.
    if (id === pathKey(row.path)) return
    setExpanded((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next })
    if (!children.has(id)) void fetchBranch(row.child)
  }, [children, fetchBranch])

  const sortNodes = useCallback((nodes: InvMatrixRow[]): InvMatrixRow[] => {
    const col = columns.find((c) => c.key === sort.key) ?? columns.find((c) => c.key === 'total')!
    const dir = sort.desc ? -1 : 1
    return [...nodes].sort((a, b) => {
      const av = col.pick(a)
      const bv = col.pick(b)
      // Unknown sorts LAST either way: not the smallest, just unknown.
      if ((av === null || av === undefined) && (bv === null || bv === undefined)) return (a.groupCode ?? '').localeCompare(b.groupCode ?? '')
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [columns, sort])

  /** Depth-first flatten of what is expanded — the rows actually drawn. */
  const rows = useMemo(() => {
    const out: DrillRow[] = []
    const walk = (path: InvDrillPath, depth: number, seen: Set<string>) => {
      const nodes = children.get(pathKey(path))
      if (!nodes) return
      const level = levelOf(path)
      sortNodes(nodes).forEach((node, index) => {
        const child = level === 'lot' ? null : childOf(path, level, node)
        const key = `${pathKey(path)}#${node.groupCode ?? '\u0000'}#${node.warehouse ?? ''}#${index}`
        out.push({ node, level, path, child, depth, key })
        const id = child ? pathKey(child) : null
        // Recurse only into a branch that is not already an ancestor.
        if (id && expanded.has(id) && !seen.has(id)) walk(child!, depth + 1, new Set(seen).add(id))
      })
    }
    walk({}, 0, new Set([rootKey]))
    return out
  }, [children, expanded, sortNodes, rootKey])

  const root = children.get(rootKey)
  const rootLoading = loading.has(rootKey)

  /* THE TOTAL, summed from the companies. Under today's model null if any
     company's figure is withheld: the sum of the visible ones would reconcile to
     nothing. Under the new model the sum of what shows, marked as partly
     withheld below, and withheld only when every company withholds it, as the
     list's totals row says it (M8). A quantity total over several units is
     shown, and says it is mixed. */
  const totals = useMemo(() => {
    if (!root || root.length === 0) return null
    const t: Record<string, number | null> = {}
    for (const c of columns) {
      if (!c.total) continue
      const vals = root.map((n) => c.pick(n))
      const nums = vals.filter((v): v is number => typeof v === 'number')
      t[c.key] = ctx.v2
        ? (nums.length ? nums.reduce((s, v) => s + v, 0) : null)
        : vals.some((v) => v === null || typeof v !== 'number') ? null : (vals as number[]).reduce((s, v) => s + v, 0)
    }
    return t
  }, [root, columns, ctx.v2])
  /* Under the new model a total that leaves out a withheld company, or adds one
     that leaves withheld lots out, says so (M8). */
  const totalsPartial = useMemo(() => {
    const out = new Set<string>()
    if (!ctx.v2 || !root) return out
    for (const c of columns) {
      const f = figureOf(c.key, value)
      if (!c.total || !f) continue
      const withheld = root.filter((n) => n[f] === null).length
      if ((withheld > 0 && withheld < root.length) || root.some((n) => partialOf(n).has(f))) out.add(c.key)
    }
    return out
  }, [root, columns, ctx.v2, value])
  const totalUnits = useMemo(() => {
    const set = new Set((root ?? []).map((n) => dimOf(n.unit, n.unitCount)?.text).filter(Boolean))
    return set.size === 1 ? [...set][0]! : set.size > 1 ? MIXED_UNITS : null
  }, [root])
  const totalCode = useMemo(() => {
    const codes = new Set((root ?? []).map((n) => n.currencyCode))
    return codes.size === 1 ? [...codes][0] : null
  }, [root])

  const buildDoc = (): ExportDoc | null => {
    if (rootLoading || rows.length === 0) return null
    const sortCol = columns.find((c) => c.key === sort.key)
    const shownCols = shown as DrillColumn[]
    return {
      report: 'Inventory Aging', page: 'Aging', title: `Inventory aging, drilled — ${value ? 'value' : 'quantity'}`,
      subtitle: 'Company, business unit, business team, product and lot — as expanded on screen.',
      context: context(ctx, [
        `Sorted by ${sortCol?.header ?? sort.key}, ${sort.desc ? 'descending' : 'ascending'} — within each level`,
        'Rows: as drilled on screen — a level appears only where its parent was opened',
      ]),
      privileged: ctx.viewAs, currency: ctx.currency,
      tables: [{
        note: 'Age counts from the arrival date. Drop-ship stock and stock with no known arrival are in their own columns, so the buckets add up to the total.'
          + (ctx.v2 && (totalsPartial.size > 0 || rows.some((r) => partialOf(r.node).size > 0)) ? ` ${PARTIAL_NOTE}` : ''),
        columns: [{ header: DRILL_CAPTION }, ...shownCols.map((c) => ({ header: c.header, kind: c.kind }))],
        rows: [
          ...rows.map<ExportRow>((r) => ({
            indent: r.depth, bold: r.depth === 0,
            cells: [nodeText(r.level, r.node), ...shownCols.map((c) => {
              if ((c.key === 'daysOld' || c.key === 'daysToExpire') && r.level !== 'lot') return null
              if (c.key === 'oldest' || c.key === 'earliest') return dateS(c.pick(r.node) as string | null)
              const v = c.pick(r.node)
              return ctx.v2 && v === null && FIGURE_KINDS.has(c.kind) ? WITHHELD : v
            })],
          })),
          ...(totals ? [{ bold: true, cells: ['Total', ...shownCols.map((c) => {
            const v = c.key in totals ? totals[c.key] : null
            return ctx.v2 && v === null && c.key in totals && FIGURE_KINDS.has(c.kind) ? WITHHELD : v
          })] } satisfies ExportRow] : []),
        ],
      }],
    }
  }

  return (
    <>
      <AgingTiles cards={cards} ctx={ctx} />
      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Inventory Aging', 'Aging']}
        title={`Stock ${value ? 'value' : 'quantity'} by age — drill down`}
        subtitle="Open a company to see its business units, then teams, products and each lot. Age counts from the arrival date; drop-ship and unknown arrival have their own columns."
        right={<ExportButtons build={buildDoc} />}
      >
        {failure ? <FailureState failure={failure} onRetry={() => setNonce((n) => n + 1)} /> : (
          <div className="flex min-h-0 flex-1 flex-col">
            <GridChrome api={layout} />
            <div className="scroll-slim min-h-0 flex-1 overflow-auto">
              <table className="report-table w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th
                      ref={memberRef}
                      scope="col"
                      className="sticky left-0 top-0 z-30 min-w-[300px] border-b border-(--color-line-strong) bg-(--color-surface) px-4 py-2.5 text-center font-semibold text-(--color-ink) @max-3xl:min-w-[10rem]"
                    >
                      <Explain surface="aging" measureKey="member">{DRILL_CAPTION}</Explain>
                    </th>
                    {(shown as DrillColumn[]).map((c) => {
                      const pin = layout.pinOf(c.key)
                      const w = layout.widthOf(c.key)
                      return (
                        <th
                          key={c.key}
                          ref={layout.headerRef(c.key)}
                          scope="col"
                          title={c.title}
                          aria-sort={sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(c.key) }}
                          onDragOver={(e) => { if (!dragKey || dragKey === c.key) return; e.preventDefault(); setOverKey(c.key) }}
                          onDrop={(e) => {
                            e.preventDefault()
                            if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                            setDragKey(null); setOverKey(null)
                          }}
                          onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                          style={{
                            width: w, minWidth: w,
                            left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                            right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                          }}
                          className={`relative sticky top-0 border-b border-l border-(--color-line) bg-(--color-surface) px-3 py-2.5 text-center font-semibold text-(--color-ink) ${
                            pin ? 'z-30' : 'z-20'
                          } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
                        >
                          <span data-head-label className="inline-block whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => setSort((s) => (s.key === c.key ? { key: c.key, desc: !s.desc } : { key: c.key, desc: true }))}
                              className="whitespace-nowrap transition-colors hover:text-(--color-accent)"
                              title={`Sort each level by ${c.header}`}
                            >
                              {c.header}
                              {sort.key === c.key && <span aria-hidden> {sort.desc ? '▼' : '▲'}</span>}
                            </button>
                            <ExplainMark surface="aging" measureKey={c.key} label={c.header} />
                          </span>
                          <ResizeHandle
                            label={c.header}
                            widthOf={() => layout.headerWidth(c.key)}
                            minWidth={() => layout.minWidthOf(c.key)}
                            onResize={(px) => layout.resize(c.key, px)}
                            onAutoFit={() => layout.autoFit(c.key)}
                          />
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rootLoading ? (
                    <tr><td colSpan={shown.length + 1} className="px-3 py-10"><Loading label="Loading stock…" /></td></tr>
                  ) : null}
                  {!rootLoading && root && root.length === 0 ? (
                    <tr>
                      <td colSpan={shown.length + 1} className="px-3 py-10 text-center text-(--color-ink-3)">
                        No lot matches this filter within what you are permitted to see.
                      </td>
                    </tr>
                  ) : null}
                  {rows.map((row, i) => {
                    const id = row.child ? pathKey(row.child) : null
                    const open = id ? expanded.has(id) : false
                    const drillFailure = id ? branchFailure.get(id) : undefined
                    const branch = id && open ? children.get(id) : undefined
                    const partial = branch && branch.length > 0 && branch[0].totalRows > branch.length
                    return (
                      <Fragment key={row.key}>
                        <DrillRowView
                          row={row} first={i === 0} open={open} loading={id ? loading.has(id) : false}
                          onToggle={() => toggle(row)} shown={shown as DrillColumn[]} layout={layout}
                        />
                        {partial ? (
                          <tr>
                            <td colSpan={shown.length + 1} className="px-3 py-2 text-(length:--text-small) text-(--color-ink-3)">
                              <span className="sticky left-3" style={{ paddingLeft: (row.depth + 1) * DRILL_INDENT_PX }}>
                                Showing {branch.length.toLocaleString()} of {branch[0].totalRows.toLocaleString()}. Narrow with the filters, or use the List view.
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {drillFailure ? (
                          <tr>
                            <td colSpan={shown.length + 1} className="px-3 pb-4">
                              <div className="sticky left-3 max-w-2xl"><FailureState failure={drillFailure} onRetry={() => toggle(row)} /></div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
                {totals ? (
                  <tfoot>
                    <tr className="bg-(--color-surface-3) font-semibold">
                      <th scope="row" className="sticky left-0 z-10 border-t-2 border-(--color-line-strong) bg-(--color-surface-3) px-4 py-2.5 text-left text-(--color-ink)">
                        Total
                      </th>
                      {(shown as DrillColumn[]).map((c) => {
                        const pin = layout.pinOf(c.key)
                        const v = c.key in totals ? totals[c.key] : undefined
                        const isQty = c.kind === 'qty'
                        return (
                          <td
                            key={c.key}
                            style={{
                              width: layout.widthOf(c.key),
                              left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                              right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                            }}
                            className={`num whitespace-nowrap border-l border-t-2 border-l-(--color-line) border-t-(--color-line-strong) px-3 py-2.5 font-semibold text-(--color-ink) ${
                              pin ? 'sticky z-[2] bg-(--color-surface-3)' : ''
                            }`}
                          >
                            {markPartial(totalsPartial.has(c.key), v === undefined ? null
                              : c.key === 'lots' ? (v ?? 0).toLocaleString()
                              : isQty
                                ? (c.key === 'total'
                                    ? num(v, totalUnits === MIXED_UNITS ? { text: MIXED_UNITS, title: mixedUnitsHint([]), mixed: true } : totalUnits ? { text: totalUnits } : null)
                                    : bucket(v, 'quantity'))
                                : c.key === 'total' || c.key === 'value'
                                  ? cash(v, totalCode ? { text: totalCode, currency: totalCode } : null)
                                  : bucket(v, 'value'))}
                          </td>
                        )
                      })}
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </div>
        )}
      </ExpandableCard>
    </>
  )
}

function DrillRowView({ row, first, open, loading, onToggle, shown, layout }: {
  row: DrillRow; first: boolean; open: boolean; loading: boolean; onToggle: () => void
  shown: DrillColumn[]
  layout: ReturnType<typeof useGridLayout<DrillColumn>>
}) {
  const { node, level, depth, child } = row
  // The separator is this row's TOP border: a sticky cell is its own layer and
  // can cover a line drawn on the row above (see ReceivablesMatrix).
  const sep = first ? '' : 'border-t'
  return (
    <tr className="group transition-colors hover:bg-(--color-navy-50)">
      <th
        scope="row"
        style={{ paddingLeft: 12 + depth * DRILL_INDENT_PX }}
        className={`sticky left-0 z-10 ${sep} border-(--color-line) bg-(--color-surface) py-2 pr-4 text-left ${depth === 0 ? 'font-semibold' : 'font-medium'} text-(--color-ink) transition-colors group-hover:bg-(--color-navy-50)`}
      >
        {child ? (
          <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-center gap-1.5 text-left hover:text-(--color-accent)">
            <span aria-hidden className="inline-block w-3 shrink-0 text-[10px] text-(--color-ink-3)">{loading ? '·' : open ? '▼' : '▶'}</span>
            <span>{nodeLabel(level, node)}</span>
          </button>
        ) : (
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="inline-block w-3 shrink-0" />
            <span>{nodeLabel(level, node)}</span>
          </span>
        )}
      </th>
      {shown.map((c) => {
        const pin = layout.pinOf(c.key)
        return (
          <td
            key={c.key}
            style={{
              width: layout.widthOf(c.key),
              left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
              right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
            }}
            className={`num ${sep} whitespace-nowrap border-l border-(--color-line) px-3 py-2 text-(--color-ink) ${
              pin ? 'sticky z-[1] bg-(--color-surface) group-hover:bg-(--color-navy-50)' : ''
            }`}
          >
            {c.render(node, level)}
          </td>
        )
      })}
    </tr>
  )
}

/* ------------------------------------------------------------------ expiry */

/**
 * THE EXPIRY LIST: V1's Expired Item List and Near To Expire, V2 and
 * Operations' Expiring In 90 / 180 Days and Expired — one list behind a
 * window. Soonest expiry first unless the reader sorts it otherwise.
 */
function ExpiryTab({ rows, cards, win, ctx }: {
  rows: InvLotRow[] | null; cards: InvOverview | null; win: InvWindow; ctx: Ctx
}) {
  const columns = useMemo<Column<InvLotRow>[]>(() => [
    { key: 'company', sortKey: 'companyname', header: 'Company', render: (r) => r.companyName ?? r.company, clamp: 26 },
    { key: 'item', sortKey: 'itemnumber', header: 'Item Number', render: (r) => r.itemNumber },
    { key: 'product', sortKey: 'productname', header: 'Product', render: (r) => r.productName, clamp: 30 },
    { key: 'lot', sortKey: 'lot', header: 'Lot', render: (r) => r.lot },
    { key: 'warehouse', sortKey: 'warehouse', header: 'Warehouse', render: (r) => r.warehouse },
    { key: 'expiry', sortKey: 'expirydate', header: 'Expiry Date', render: (r) => date(r.expiryDate) },
    { key: 'daysToExpire', sortKey: 'daystoexpire', header: 'Days to Expire', align: 'right', render: (r) => toExpire(r.daysToExpire, r.expiryDate) },
    { key: 'quantity', sortKey: 'quantity', header: 'Quantity', align: 'right', render: (r) => num(r.quantity, dimOf(r.unit)) },
    ...(ctx.canSeeCost ? [
      /* As Power BI V1's Expired Item List and Near To Expire pages showed it. */
      { key: 'unitCost', sortKey: 'unitcost', header: 'Unit Cost', align: 'right' as const, render: (r: InvLotRow) => price(r.unitCost, curOf(r.currencyCode)) },
      {
        key: 'value', sortKey: 'value', header: 'Value', align: 'right' as const,
        render: (r: InvLotRow) => cash(r.value, curOf(r.currencyCode)), value: (r: InvLotRow) => r.value ?? 0, total: true,
        // A lot is a detail row: its value is shown or withheld, never partial (054).
        ...(ctx.v2 ? { withheld: (r: InvLotRow) => (r.value === null ? 'withheld' as const : null) } : {}),
      },
    ] : []),
    { key: 'arrival', sortKey: 'arrivaldate', header: 'Arrival Date', render: (r) => date(r.arrivalDate) },
    { key: 'daysOld', sortKey: 'daysold', header: 'Days Old', align: 'right', render: (r) => daysOld(r.daysOld) },
    { key: 'bu', sortKey: 'businessunit', header: 'Business Unit', render: (r) => r.businessUnit },
    { key: 'bt', sortKey: 'businessteam', header: 'Business Team', render: (r) => r.businessTeam },
    { key: 'supplier', sortKey: 'supplier', header: 'Supplier', render: (r) => r.supplier, clamp: 24 },
    { key: 'unit', sortKey: 'unit', header: 'Unit', render: (r) => r.unit },
  ], [ctx.canSeeCost, ctx.v2])

  const buildDoc = () => !rows?.length ? null : gridDoc<InvLotRow>(ctx, 'inv-expiry', 'Expiry', `Lots — ${WINDOW_LABEL[win].toLowerCase()}`,
    WINDOW_TITLE[win] + '.', [
    { header: 'Company', cell: (r) => r.companyName ?? r.company },
    { header: 'Item Number', cell: (r) => r.itemNumber },
    { header: 'Product', cell: (r) => r.productName },
    { header: 'Lot', cell: (r) => r.lot },
    { header: 'Warehouse', cell: (r) => r.warehouse },
    { header: 'Expiry Date', cell: (r) => dateS(r.expiryDate) },
    { header: 'Days to Expire', kind: 'int', cell: (r) => (isPlaceholder(r.expiryDate) ? null : r.daysToExpire) },
    { header: 'Quantity', kind: 'qty', cell: (r) => r.quantity },
    ...(ctx.canSeeCost ? [
      { header: 'Unit Cost', kind: 'num2' as CellKind, cell: (r: InvLotRow) => r.unitCost },
      { header: 'Value', kind: 'money' as CellKind, cell: (r: InvLotRow) => r.value },
    ] : []),
    { header: 'Arrival Date', cell: (r) => dateS(r.arrivalDate) },
    { header: 'Days Old', kind: 'int', cell: (r) => r.daysOld },
    { header: 'Business Unit', cell: (r) => r.businessUnit },
    { header: 'Business Team', cell: (r) => r.businessTeam },
    { header: 'Supplier', cell: (r) => r.supplier },
    { header: 'Unit', cell: (r) => r.unit },
    ...(ctx.canSeeCost ? [{ header: 'Currency', cell: (r: InvLotRow) => r.currencyCode }] : []),
  ], rows, rows[0]?.totalRows, [`Expiry: ${WINDOW_LABEL[win]}`])

  const u = dimOf(cards?.unit, cards?.unitCount)
  const c = curOf(cards?.currencyCode, cards?.currencyCount)
  const money_ = ctx.canSeeCost
  const p = partialOf(cards)
  const tile = (key: string, label: string, lotsN: number | null | undefined, qty: number | null | undefined, val: number | null | undefined, tone?: 'bad') =>
    <StatTile
      surface="expiry" measureKey={key} label={label}
      value={(lotsN ?? 0).toLocaleString()}
      secondary={money_ ? withCur(`lots · ${partS(cashS(val, ctx.v2), p.has(`${key}Value`))}`, c) : withDim(`lots · ${partS(numS(qty, ctx.v2), p.has(`${key}Qty`))}`, u)}
      tone={tone && lotsN ? tone : undefined}
      dense
    />
  return (
    <>
      <CollapsibleStats storageKey="stats:inventory-aging:expiry">
        <div className="stat-strip">
          {tile('expired', 'Expired', cards?.expiredLots, cards?.expiredQty, cards?.expiredValue, 'bad')}
          {tile('within90', 'Within 90 days', cards?.within90Lots, cards?.within90Qty, cards?.within90Value)}
          {tile('within180', 'Within 180 days', cards?.within180Lots, cards?.within180Qty, cards?.within180Value)}
          <StatTile surface="expiry" measureKey="noExpiry" label="No expiry date" value={(cards?.noExpiryLots ?? 0).toLocaleString()} secondary="lots" dense />
        </div>
      </CollapsibleStats>
      {win === 'expired' ? (
        <p className="text-(length:--text-small) text-(--color-ink-3)">
          Expired means an expiry date before today. Lots dated 1 Jan 1900 — a placeholder in the source, not a real date — are counted here, as Power BI counted them.
        </p>
      ) : null}

      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Inventory Aging', 'Expiry']}
        title={`Lots — ${WINDOW_LABEL[win].toLowerCase()}`}
        subtitle={win === 'expired' || win === 'noexpiry' || win === 'all'
          ? `${WINDOW_TITLE[win]}.`
          : `${WINDOW_TITLE[win]}. Soonest first.`}
        right={<ExportButtons build={buildDoc} />}
      >
        {!rows ? <Loading label="Loading lots…" />
          : rows.length === 0 ? <EmptyState title="No lots in this window" detail="Nothing matches this filter and expiry window within what you are permitted to see." />
          : <DataGrid
              surface="expiry" gridId="inv-expiry" columns={columns} rows={rows}
              totalsLabel="This page"
              totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill
            />}
      </ExpandableCard>
    </>
  )
}

/* -------------------------------------------------------------------- lots */

/**
 * EVERY LOT, EVERY ATTRIBUTE: Operations' Stock Detailed By Lot and V1's
 * Inventory-Product. The bands are the procedure's, named here; the 1900
 * expiry placeholder is said to be one.
 */
function LotsTab({ rows, cards, ctx }: { rows: InvLotRow[] | null; cards: InvOverview | null; ctx: Ctx }) {
  const columns = useMemo<Column<InvLotRow>[]>(() => [
    { key: 'company', sortKey: 'companyname', header: 'Company', render: (r) => r.companyName ?? r.company, clamp: 26 },
    { key: 'item', sortKey: 'itemnumber', header: 'Item Number', render: (r) => r.itemNumber },
    { key: 'product', sortKey: 'productname', header: 'Product', render: (r) => r.productName, clamp: 30 },
    { key: 'lot', sortKey: 'lot', header: 'Lot', render: (r) => r.lot },
    { key: 'warehouse', sortKey: 'warehouse', header: 'Warehouse', render: (r) => r.warehouse },
    { key: 'quantity', sortKey: 'quantity', header: 'Quantity', align: 'right', render: (r) => num(r.quantity, dimOf(r.unit)) },
    ...(ctx.canSeeCost ? [
      { key: 'unitCost', sortKey: 'unitcost', header: 'Unit Cost', align: 'right' as const, render: (r: InvLotRow) => price(r.unitCost, curOf(r.currencyCode)) },
      {
        key: 'value', sortKey: 'value', header: 'Value', align: 'right' as const,
        render: (r: InvLotRow) => cash(r.value, curOf(r.currencyCode)), value: (r: InvLotRow) => r.value ?? 0, total: true,
        ...(ctx.v2 ? { withheld: (r: InvLotRow) => (r.value === null ? 'withheld' as const : null) } : {}),
      },
    ] : []),
    { key: 'age', sortKey: 'ageband', header: 'Age', render: (r) => (r.ageBand ? AGE_LABEL[r.ageBand] : null) },
    { key: 'daysOld', sortKey: 'daysold', header: 'Days Old', align: 'right', render: (r) => daysOld(r.daysOld) },
    { key: 'expiryBand', sortKey: 'expiryband', header: 'Expiry', render: (r) => (r.expiryBand ? EXPIRY_LABEL[r.expiryBand] : null) },
    { key: 'daysToExpire', sortKey: 'daystoexpire', header: 'Days to Expire', align: 'right', render: (r) => toExpire(r.daysToExpire, r.expiryDate) },
    { key: 'mfg', sortKey: 'mfgdate', header: 'Manufactured', render: (r) => date(r.mfgDate) },
    { key: 'arrival', sortKey: 'arrivaldate', header: 'Arrival Date', render: (r) => date(r.arrivalDate) },
    { key: 'expiry', sortKey: 'expirydate', header: 'Expiry Date', render: (r) => date(r.expiryDate) },
    { key: 'bestBefore', sortKey: 'bestbefore', header: 'Best Before', render: (r) => date(r.bestBefore) },
    { key: 'shelfAdvice', sortKey: 'shelfadvice', header: 'Shelf Advice', render: (r) => date(r.shelfAdvice) },
    { key: 'bu', sortKey: 'businessunit', header: 'Business Unit', render: (r) => r.businessUnit },
    { key: 'bt', sortKey: 'businessteam', header: 'Business Team', render: (r) => r.businessTeam },
    { key: 'supplier', sortKey: 'supplier', header: 'Supplier', render: (r) => r.supplier, clamp: 24 },
    { key: 'description', sortKey: 'description', header: 'Description', render: (r) => r.description, clamp: 36 },
    { key: 'unit', sortKey: 'unit', header: 'Unit', render: (r) => r.unit },
    ...(ctx.canSeeCost ? [{ key: 'currency', sortKey: 'currencycode', header: 'Currency', render: (r: InvLotRow) => currencyMark(r.currencyCode) }] : []),
  ], [ctx.canSeeCost, ctx.v2])

  const buildDoc = () => !rows?.length ? null : gridDoc<InvLotRow>(ctx, 'inv-lots', 'Lots', 'Every lot',
    'Every lot with every attribute it carries.', [
    { header: 'Company', cell: (r) => r.companyName ?? r.company },
    { header: 'Item Number', cell: (r) => r.itemNumber },
    { header: 'Product', cell: (r) => r.productName },
    { header: 'Lot', cell: (r) => r.lot },
    { header: 'Warehouse', cell: (r) => r.warehouse },
    { header: 'Quantity', kind: 'qty', cell: (r) => r.quantity },
    ...(ctx.canSeeCost ? [
      { header: 'Unit Cost', kind: 'num2' as CellKind, cell: (r: InvLotRow) => r.unitCost },
      { header: 'Value', kind: 'money' as CellKind, cell: (r: InvLotRow) => r.value },
    ] : []),
    { header: 'Age', cell: (r) => (r.ageBand ? AGE_LABEL[r.ageBand] : null) },
    { header: 'Days Old', kind: 'int', cell: (r) => r.daysOld },
    { header: 'Expiry', cell: (r) => (r.expiryBand ? EXPIRY_LABEL[r.expiryBand] : null) },
    { header: 'Days to Expire', kind: 'int', cell: (r) => (isPlaceholder(r.expiryDate) ? null : r.daysToExpire) },
    { header: 'Manufactured', cell: (r) => dateS(r.mfgDate) },
    { header: 'Arrival Date', cell: (r) => dateS(r.arrivalDate) },
    { header: 'Expiry Date', cell: (r) => dateS(r.expiryDate) },
    { header: 'Best Before', cell: (r) => dateS(r.bestBefore) },
    { header: 'Shelf Advice', cell: (r) => dateS(r.shelfAdvice) },
    { header: 'Business Unit', cell: (r) => r.businessUnit },
    { header: 'Business Team', cell: (r) => r.businessTeam },
    { header: 'Supplier', cell: (r) => r.supplier },
    { header: 'Description', cell: (r) => r.description },
    { header: 'Unit', cell: (r) => r.unit },
    ...(ctx.canSeeCost ? [{ header: 'Currency', cell: (r: InvLotRow) => r.currencyCode }] : []),
  ], rows, rows[0]?.totalRows)

  const u = dimOf(cards?.unit, cards?.unitCount)
  const c = curOf(cards?.currencyCode, cards?.currencyCount)
  return (
    <>
      <CollapsibleStats storageKey="stats:inventory-aging:lots">
        <div className="stat-strip">
          <StatTile surface="lots" measureKey="lots" label="Lots" value={(cards?.lots ?? 0).toLocaleString()} secondary={`${(cards?.items ?? 0).toLocaleString()} items · ${(cards?.companies ?? 0).toLocaleString()} companies`} tone="accent" dense />
          <StatTile surface="lots" measureKey="quantity" label="Quantity" value={partS(numS(cards?.quantity, ctx.v2), partialOf(cards).has('quantity'))} fullValue={partF(fullQty(cards?.quantity, ctx.v2), partialOf(cards).has('quantity'))} secondary={withDim(undefined, u)} dense />
          {ctx.canSeeCost ? (
            <StatTile surface="lots" measureKey="value" label="Value" value={partS(cashS(cards?.value, ctx.v2), partialOf(cards).has('value'))} fullValue={partF(fullCash(cards?.value, ctx.v2), partialOf(cards).has('value'))} secondary={withCur(undefined, c)} dense />
          ) : null}
        </div>
      </CollapsibleStats>

      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Inventory Aging', 'Lots']}
        title="Every lot"
        subtitle="One row per lot in each warehouse, with every date it carries. Negative stock is real and shown."
        right={<ExportButtons build={buildDoc} />}
      >
        {!rows ? <Loading label="Loading lots…" />
          : rows.length === 0 ? <EmptyState title="No lots to show" detail="No lot matches this filter within what you are permitted to see." />
          : <DataGrid
              surface="lots" gridId="inv-lots" columns={columns} rows={rows}
              totalsLabel="This page"
              totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill
            />}
      </ExpandableCard>
    </>
  )
}
